/* eslint-disable max-lines */
/* eslint-disable no-restricted-syntax */
import { createSelector } from 'reselect';
// ---- external imports ----

import { productsSelectors, employeesSelectors, customerSelectors } from '~/store';
import { getSellingPriceFor } from '~/store/products/selectors';
import { CUSTOMER_TYPES } from '~/lib/constants';
import { CUSTOMER_ANIMATION_STATE } from '~/containers/game-user/customers-wrapper/animationConstants';
// ---- internal imports ----

const customersViewingSelector = (state) => state.customers.viewing;

function getCurrentCustomers(state) {
	const customers = getTurnCustomers(state);
	if (!customers || customers?.length === 0) {
		return [];
	}

	return state.ui.customerToViewIndices.map((customerIndex) => customers[customerIndex]);
}

function hasAlreadyProcessedCustomer(animationState) {
	return !(animationState === CUSTOMER_ANIMATION_STATE.IDLE
		|| animationState === CUSTOMER_ANIMATION_STATE.WAITING_TO_APPEAR);
}

function isCustomerPriceValid(state, customer) {
	let isPriceValid = false;
	switch(customer.type) {
	case CUSTOMER_TYPES.CREDIT:
	case CUSTOMER_TYPES.LAZY:
	case CUSTOMER_TYPES.WHATEVER_PRICE:
		isPriceValid = true;
		break;
	case CUSTOMER_TYPES.FIXED_PRICE:
		const hasNegotiatorHired = employeesSelectors.hasNegotiator(state);
		const customerPrice = hasNegotiatorHired
			? customer.negotiatedPrice
			: customer.offerPrice;
		isPriceValid = 
			customerPrice >= getSellingPriceFor(state, customer.productName);					
		break;
	case CUSTOMER_TYPES.BARGAINING:
		//	Bargaining customers have successful bargains, if their offer price is valid
		isPriceValid = 
			customer.offerPrice >= getSellingPriceFor(state, customer.productName);
	default:
		break;
	}
	return isPriceValid;
}

export function getTurnCustomers(state) {
	return state.customers.turnCustomers;
}

// here
export function hasCustomers(state) {
	return customersViewing(state).length > 0;
}
export function customersWaiting(state) {
	return state.customers.waiting;
}

// here
export  const customersViewing = createSelector(
	customersViewingSelector, 
	(customers) => customers
);

export function getCustomerByIndex(storeState, index) {
	if (customersViewing(storeState).length > 0) {
		return customersViewing(storeState)[index];
	}
	else {
		return null;
	}
}

export function getCustomersAnimatedVisibleCount(state) {
	return state.customers.customersAnimatedVisibleCount;
}

export function hasCustomersEntryAnimationEnded(state) {
	const visibleCustomerCount = getCustomersAnimatedVisibleCount(state);
	const viewingCustomersCount = customersViewing(state).filter((cust) => cust !== null).length;

	return visibleCustomerCount >= viewingCustomersCount;
}

export function isExtraCustomersButtonVisible(state) {
	const extraCustomersCount = extraCustomerCount(state);

	if (extraCustomersCount === 0) {
		return false;
	}

	const isCustomerEntryAnimationComplete = isViewingCustomersEntryAnimationComplete(state);

	if (!isCustomerEntryAnimationComplete) {
		return false;
	}

	const hasProductsInStock = productsSelectors.hasProductsInStock(state);

	if (!hasProductsInStock) {
		return true;
	}

	const hasSalesmanHired = employeesSelectors.hasSalesman(state);

	if (!hasSalesmanHired) {
		return true;
	}

	return false;
}

export function canNegotiateCustomers(state, customers) {
	let isCustomersNegotationsNeeded = false;
	const filteredCustomers = customers.filter((customer) => customer !== null);

	filteredCustomers.every((customer) => {
		const hasHiredNegotiator = customer.negotiatedPrice > customer.offerPrice;
		const hasLowerPriceThanSelling = 
			customer.offerPrice < getSellingPriceFor(state, customer.productName);
		const hasProductInStock = 
			productsSelectors.getProduct(state, customer.productName).amount > 0;
		
		if (hasHiredNegotiator && hasLowerPriceThanSelling && hasProductInStock) {
			isCustomersNegotationsNeeded = true;
			return false;
		}
		return true;
	});

	return isCustomersNegotationsNeeded;
}

export function getCustomersToDisplay(state) {
	const customers = state.customers.viewing
			?.map?.((customer, id) => ({ ...customer, id: id }));
			
	return customers ?? [];
}

export function allCurrentCustomersProcessed(state) {
	return getCurrentCustomers(state)
		?.every?.((customer) => customer.isDismissed);
}

// here
export function hasExtraCustomers(state) {
	return customersWaiting(state).length > 0;
}

export function extraCustomerCount(state) {
	return customersWaiting(state).length;
}

export function getNumDismissed(state) {
	return state.customers.numDismissed;
}

export function getViewingCustomersSalesCount(state) {
	return state.customers.viewingCustomersSalesCount;
}

export function getValidViewingCustomersCount(state) {
	return state.customers?.validViewingCustomers?.length;
}

export function getValidCustomersFromViewing(state, viewing = []) {
	if (viewing.length === 0) {
		return state.customers.validViewingCustomers;
	}

	const validViewing =  viewing.filter((customer) => 
		customer !== null
		&& !hasAlreadyProcessedCustomer(customer.animationState)
		&& isCustomerPriceValid(state, customer)
		&& productsSelectors.getProduct(state, customer.productName).amount > 0
	);

	return validViewing;
}

export function hasValidViewingCustomers(state) {
	return getValidViewingCustomersCount(state) > 0;
}

export function getInvalidViewedCustomersCountForMonth(state) {
	return state.customers.invalidCustomersCountForMonth;
}

export function canNegotiatorWorkWithViewingCustomers(state) {
	return state.customers.canNegotiatorWorkWithViewingCustomers;
}

export function isExtraCustomersBtnDisabled(state) {
	const isCustomersEmployeeActive = employeesSelectors
		.isCustomersEmployeeActive(state);

	const hasValidViewingCustomers = customerSelectors.hasValidViewingCustomers(state);

	const isDisabled = hasValidViewingCustomers
			|| isCustomersEmployeeActive;

	return isDisabled;
}

export function getEntryAnimationCompleteCount(state) {
	return state.customers.customersEntryAnimationCompleteCount;
}

export function isViewingCustomersEntryAnimationComplete(state) {
	return state.customers.isViewingCustomersEntryAnimationComplete;
}

export function hasPendingBargaining(state) {
	return state.customers.pendingBargainingCount > 0;
}
